import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

export default function BookingModal({ opening, open, onOpenChange, onSubmitted }) {
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  if (!opening) return null;

  const started = new Date(opening.start_time).getTime() <= Date.now();

  const submit = async (e) => {
    e.preventDefault();
    if (!phone || phone.length < 5) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!name.trim()) {
      toast.error("Please enter your name so the business knows who's coming in");
      return;
    }
    // Email is required (Feb 2026) so we can always send the customer their
    // booking-confirmed email once the salon approves. Basic client-side
    // sanity check; the server-side EmailStr validator is the source of truth.
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      toast.error("Please enter a valid email so we can send your confirmation");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/openings/${opening.id}/requests`, {
        phone,
        name: name.trim(),
        email: trimmedEmail,
        note,
      });
      toast.success("Request sent — you'll be redirected to your status page.");
      onOpenChange(false);
      onSubmitted && onSubmitted();
      setPhone("");
      setName("");
      setEmail("");
      setNote("");
      navigate(`/status/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="booking-modal"
        className="border-border rounded-none max-w-md p-0 overflow-hidden gap-0"
      >
        <div className="border-b border-border bg-background px-6 py-4">
          <DialogHeader>
            <p className="mono text-[10px] uppercase tracking-[0.25em] text-[#C9A85F]">
              Request to book
            </p>
            <DialogTitle className="mt-1 text-2xl leading-tight tracking-tight">
              {opening.title}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {opening.business_name} · {opening.address}
            </DialogDescription>
          </DialogHeader>
        </div>

        <form onSubmit={submit} className="grid gap-4 p-6">
          {started && (
            <div
              data-testid="booking-closed-banner"
              className="border border-[#F87171] bg-[#F87171]/5 p-3 text-xs text-[#F87171]"
            >
              This appointment has already started — bookings are closed.
            </div>
          )}
          <div className="grid gap-1.5">
            <Label htmlFor="bk-phone" className="mono text-[10px] uppercase tracking-[0.2em]">
              Phone number *
            </Label>
            <Input
              id="bk-phone"
              data-testid="booking-phone-input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1 415 555 0100"
              type="tel"
              className="rounded-none"
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="bk-name" className="mono text-[10px] uppercase tracking-[0.2em]">
              Your name *
            </Label>
            <Input
              id="bk-name"
              data-testid="booking-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane"
              className="rounded-none"
              required
            />
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="bk-email" className="mono text-[10px] uppercase tracking-[0.2em]">
              Email
            </Label>
            <Input
              id="bk-email"
              data-testid="booking-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="rounded-none"
              autoComplete="email"
              required
            />
            <p className="mono text-[10px] text-muted-foreground">
              We'll send your booking confirmation here once the salon approves.
            </p>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="bk-note" className="mono text-[10px] uppercase tracking-[0.2em]">
              Short note (optional)
            </Label>
            <Textarea
              id="bk-note"
              data-testid="booking-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything the business should know?"
              rows={3}
              className="rounded-none resize-none"
            />
          </div>

          <button
            type="submit"
            data-testid="booking-submit-btn"
            disabled={busy || started}
            className="mt-1 w-full gold-metallic-bg px-4 py-3 text-sm font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {started ? "Bookings closed" : busy ? "Sending…" : "Send Request"}
          </button>
          <p className="text-[11px] text-muted-foreground">
            No payment now. The business will approve or decline within minutes.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
